import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { push, replace } from 'connected-react-router';
import { IApplicationState } from '../store';
import { resolveLocale, looksLikeLocale, replaceLanguageInPath } from '../helpers/lang';
import { ensureLocalePath } from '../helpers/routes';
import { actionCreators } from '../store/Internationalisation';

// Determines what action to take based on URL locale, store locale, and whether the store just changed.
// Exported for testing -- the hook is a thin React wrapper around this decision function.
export type SyncAction =
	| { type: 'push'; path: string }
	| { type: 'replace'; path: string; setLanguage: string }
	| { type: 'setLanguage'; locale: string }
	| { type: 'ensureInit'; locale: string };

export function resolveLanguageSync(
	pathname: string,
	search: string,
	storeLocale: string,
	prevLocale: string
): SyncAction {
	const firstSegment = pathname.split('/').filter((s) => s !== '')[0] ?? '';
	const urlLocale = resolveLocale(firstSegment);
	const storeLocaleChanged = prevLocale !== storeLocale;

	console.log('[langSync] resolve:', { pathname, firstSegment, urlLocale, storeLocale, prevLocale, storeLocaleChanged });

	// Store changed (language switcher) and URL hasn't caught up -> URL follows store
	if (storeLocaleChanged && urlLocale !== storeLocale) {
		const newPath = ensureLocalePath(storeLocale, pathname, search);
		console.log('[langSync] -> push (store changed, URL behind):', newPath);
		return { type: 'push', path: newPath };
	}

	// URL has a locale that disagrees with store -> store follows URL (external nav / shared link)
	if (urlLocale && urlLocale !== storeLocale) {
		console.log('[langSync] -> setLanguage (URL disagrees with store):', urlLocale);
		return { type: 'setLanguage', locale: urlLocale };
	}

	// Segment looks like a locale but isn't in our list (e.g. /ko/book-now) -> strip it, default to store
	if (!urlLocale && looksLikeLocale(firstSegment)) {
		const newPath = replaceLanguageInPath(pathname, storeLocale) + search;
		console.log('[langSync] -> replace (unrecognised locale-like segment):', newPath);
		return { type: 'replace', path: newPath, setLanguage: storeLocale };
	}

	// No locale in URL -> prepend store's locale silently, ensure language systems initialised
	if (!urlLocale) {
		const newPath = ensureLocalePath(storeLocale, pathname, search);
		console.log('[langSync] -> replace (no locale in URL):', newPath);
		return { type: 'replace', path: newPath, setLanguage: storeLocale };
	}

	// URL locale matches store but has wrong case (e.g. /FR/ instead of /fr/) -> silently fix
	if (firstSegment !== urlLocale) {
		const newPath = ensureLocalePath(storeLocale, pathname, search);
		console.log('[langSync] -> replace (normalise segment):', firstSegment, '->', urlLocale);
		return { type: 'replace', path: newPath, setLanguage: storeLocale };
	}

	// URL and store agree -- ensure language systems are initialised on first run
	// (thunk's languageInitialised guard makes this a no-op after boot)
	console.log('[langSync] -> ensureInit (URL and store agree)');
	return { type: 'ensureInit', locale: storeLocale };
}

// Sole owner of bidirectional sync between URL locale prefix and Redux language state.
// Uses a ref to detect which side changed so it can resolve conflicts correctly:
//   Store changed (language switcher) -> URL follows store (push, so back button undoes it)
//   URL changed (navigation / shared link) -> store follows URL
//   URL missing locale -> silently prepend store's locale (replace, no history entry)
const useUrlLanguageSync = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const selectedLanguage = useSelector(
		(state: IApplicationState) => state.intl.selectedLanguage
	);
	const prevLocaleRef = useRef(selectedLanguage.momentLocale);

	useEffect(() => {
		const storeLocale = selectedLanguage.momentLocale;
		const action = resolveLanguageSync(
			location.pathname,
			location.search,
			storeLocale,
			prevLocaleRef.current
		);
		prevLocaleRef.current = storeLocale;

		console.log('[langSync] effect dispatching:', action.type);
		switch (action.type) {
			case 'push':
				dispatch(push(action.path));
				break;
			case 'replace':
				dispatch(actionCreators.setAppLanguage(action.setLanguage));
				dispatch(replace(action.path));
				break;
			case 'setLanguage':
				dispatch(actionCreators.setAppLanguage(action.locale));
				break;
			case 'ensureInit':
				dispatch(actionCreators.setAppLanguage(action.locale));
				break;
		}
	}, [dispatch, location.pathname, location.search,
		selectedLanguage.momentLocale, selectedLanguage.code]);
};

export default useUrlLanguageSync;
